import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useWalletModal } from '../context/WalletModalContext';

// Edit labels/order here. `to` is kept on each item so it's a one-line
// change to switch back to real navigation later — see openItem() below.
const MENU_ITEMS = [
  { label: 'Migration', to: '/' },
  { label: 'Recovery', to: '/recovery' },
  { label: 'Claim Rewards', to: '/claimrewards' },
  { label: 'Claim Airdrop', to: '/claimairdrop' },
  { label: 'Snapshot Registration', to: '/snapshotregistration' },
  { label: 'Slippage', to: '/slippage' },
  { label: 'Cross Transfer', to: '/crosstransfer' },
  { label: 'Fix Staking Issue', to: '/fixstakingissue' },
  { label: 'Fix Transaction Issue', to: '/fixtransactionissue' },
  { label: 'Missing/Irregular Balance', to: '/missingorirregularbalance' },
  { label: 'Restore Full Functionality', to: '/restorefullfunctionality' },
  { label: 'Connect Wallet', to: '/connectwallet' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { openWallet } = useWalletModal();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Placeholder behavior: every menu item opens the wallet modal
  // instead of navigating, until real destination pages exist.
  // To restore real navigation later, swap this button back to
  // <Link to={item.to}> for whichever items have real pages.
  function handleItemClick() {
    setOpen(false);
    openWallet();
  }

  return (
    <>
      <style>{`
        .dm-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 64px;
          padding: 0 20px;
          background: #ffffff;
          border-bottom: 1px solid #ececec;
          box-sizing: border-box;
        }
        .dm-brand {
          display: flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
          color: #111111;
        }
        .dm-brand-name {
          font-size: 19px;
          font-weight: 700;
          letter-spacing: -0.01em;
        }
        .dm-hamburger {
          width: 44px;
          height: 44px;
          border-radius: 22px;
          background: #16171a;
          border: none;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
          cursor: pointer;
          flex-shrink: 0;
          -webkit-tap-highlight-color: transparent;
        }
        .dm-hamburger span {
          display: block;
          width: 18px;
          height: 2px;
          background: #ffffff;
          border-radius: 2px;
          transition: transform 0.2s ease, opacity 0.2s ease;
        }
        .dm-hamburger.open span:nth-child(1) {
          transform: translateY(6px) rotate(45deg);
        }
        .dm-hamburger.open span:nth-child(2) {
          opacity: 0;
        }
        .dm-hamburger.open span:nth-child(3) {
          transform: translateY(-6px) rotate(-45deg);
        }

        .dm-overlay {
          position: fixed;
          inset: 0;
          top: 64px;
          background: rgba(0, 0, 0, 0.35);
          z-index: 998;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.25s ease;
        }
        .dm-overlay.open {
          opacity: 1;
          pointer-events: auto;
        }

        .dm-menu {
          position: fixed;
          top: 64px;
          right: 0;
          bottom: 0;
          width: 320px;
          max-width: 85vw;
          background: #ffffff;
          border-left: 1px solid #ececec;
          z-index: 999;
          transform: translateX(100%);
          transition: transform 0.28s ease;
          overflow-y: auto;
          padding: 20px 20px calc(20px + env(safe-area-inset-bottom));
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .dm-menu.open {
          transform: translateX(0);
        }
        .dm-menu button {
          display: block;
          width: 100%;
          padding: 14px 22px;
          font-size: 15px;
          font-weight: 600;
          color: #ffffff;
          background: #111111;
          border: none;
          border-radius: 999px;
          text-align: center;
          font-family: inherit;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
          transition: opacity 0.15s ease;
        }
        .dm-menu button:active {
          opacity: 0.85;
        }

        @media (max-width: 480px) {
          .dm-header { height: 56px; padding: 0 14px; }
          .dm-brand-name { font-size: 17px; }
          .dm-hamburger { width: 40px; height: 40px; }
          .dm-overlay, .dm-menu { top: 56px; }
          .dm-menu { width: 100vw; max-width: 100vw; border-left: none; padding: 16px 16px calc(16px + env(safe-area-inset-bottom)); gap: 10px; }
          .dm-menu button { padding: 13px 20px; font-size: 14.5px; }
        }
      `}</style>

      <header className="dm-header">
        <Link to="/" className="dm-brand">
          <Logo />
          <span className="dm-brand-name">Dev Mainnet</span>
        </Link>

        <button
          type="button"
          className={`dm-hamburger${open ? ' open' : ''}`}
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
        >
          <span />
          <span />
          <span />
        </button>
      </header>

      <div
        className={`dm-overlay${open ? ' open' : ''}`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />

      <nav className={`dm-menu${open ? ' open' : ''}`}>
        {MENU_ITEMS.map((item) => (
          <button key={item.to} type="button" onClick={handleItemClick}>
            {item.label}
          </button>
        ))}
      </nav>
    </>
  );
}

function Logo() {
  return (
    <svg width="26" height="26" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path
        d="M4 20l6-9 6 9 6-9 6 9"
        stroke="#111111"
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}